import { z } from 'zod';
import { ChannelKindSchema, ChannelAccountStatusSchema, GmailChannelConfigSchema as GmailChannelConfigBase, WebFormChannelConfigSchema as WebFormChannelConfigBase, CHANNEL_SECRET_KEYS, } from '@wit-support/contracts';
/**
 * Admin-facing Zod schemas for channel CRUD.
 *
 * The per-kind config shapes derive from the canonical schemas in
 * `@wit-support/contracts` (`GmailChannelConfigSchema` / `WebFormChannelConfigSchema`),
 * layering only `.meta()` UI labels on top via `.extend()` + per-field
 * `.meta()` calls — `.meta()` returns a clone, so the canon itself is never
 * mutated. Validation semantics (accept/reject) are identical to the canon
 * and to `@wit-support/app-runtime/channel-configs`, which re-exports the
 * same canon under its own historical names. admin-contracts stays
 * lightweight enough to ship in the Vite browser bundle by depending only on
 * `@wit-support/contracts` (zod-only) rather than `@wit-support/app-runtime`,
 * which pulls in ioredis/bullmq/pino that cannot be bundled for the browser.
 */
export const ChannelKind = ChannelKindSchema.meta({
    label: 'Kind',
    options: [
        { value: 'gmail', label: 'Gmail' },
        { value: 'imap', label: 'IMAP' },
        { value: 'smtp_inbound', label: 'SMTP inbound' },
        { value: 'web_form', label: 'Web form' },
    ],
});
export const ChannelAccountStatus = ChannelAccountStatusSchema.meta({
    label: 'Status',
    options: [
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'reauth_required', label: 'Reauth required' },
        { value: 'error', label: 'Error' },
        { value: 'pending_oauth', label: 'Pending OAuth' },
    ],
});
export const GmailChannelConfigSchema = GmailChannelConfigBase.extend({
    clientId: GmailChannelConfigBase.shape.clientId.meta({
        label: 'OAuth client ID',
        description: 'From Google Cloud Console.',
    }),
    clientSecret: GmailChannelConfigBase.shape.clientSecret.meta({
        label: 'OAuth client secret',
        secret: true,
    }),
    refreshToken: GmailChannelConfigBase.shape.refreshToken.meta({
        label: 'Refresh token',
        description: 'Obtained once during OAuth consent.',
        secret: true,
    }),
    emailAddress: GmailChannelConfigBase.shape.emailAddress.meta({
        label: 'Mailbox address',
        description: 'The Google account this channel polls.',
    }),
    pollIntervalMs: GmailChannelConfigBase.shape.pollIntervalMs.meta({
        label: 'Poll interval (ms)',
        description: 'How often to call history.list. 5s–60s.',
    }),
    sendFromAlias: GmailChannelConfigBase.shape.sendFromAlias.meta({
        label: 'Send-from alias',
        description: 'Optional alias to use on outbound replies.',
    }),
    fromName: GmailChannelConfigBase.shape.fromName.meta({
        label: 'From display name',
        description: 'Optional name shown on outbound replies, e.g. WIT LAB <info@witlab.us>.',
    }),
});
export const WebFormChannelConfigSchema = WebFormChannelConfigBase.extend({
    recipientHint: WebFormChannelConfigBase.shape.recipientHint.meta({
        label: 'Recipient hint',
        description: 'Displayed to users as the apparent destination.',
    }),
    webhookSecret: WebFormChannelConfigBase.shape.webhookSecret.meta({
        label: 'Webhook secret',
        description: 'Shared HMAC-SHA256 secret used to sign inbound payloads.',
        secret: true,
    }),
    allowAttachments: WebFormChannelConfigBase.shape.allowAttachments.meta({
        label: 'Allow attachments',
    }),
});
// Keys of each kind's config that are considered secret and must be redacted
// when returned to callers without `channels.read_secrets`.
export const SECRET_KEYS = CHANNEL_SECRET_KEYS;
export const REDACTED = '••••';
/**
 * Discriminated-union describing the per-kind config payload (including the
 * `kind` itself as discriminator). Nested under `CreateChannelRequest.config`
 * so the request's root schema is a plain ZodObject — that keeps ZodField
 * path resolution simple (root-level discriminated union is not well-supported
 * by the form engine).
 *
 * Only `gmail` and `web_form` are supported today; `imap` / `smtp_inbound`
 * values exist in the DB enum but have no adapter, so they're omitted.
 */
export const ChannelConfigUnion = z.discriminatedUnion('kind', [
    GmailChannelConfigSchema.extend({
        kind: z.literal('gmail').meta({ label: 'Gmail' }),
    }).meta({ label: 'Gmail' }),
    WebFormChannelConfigSchema.extend({
        kind: z.literal('web_form').meta({ label: 'Web form' }),
    }).meta({ label: 'Web form' }),
]);
export const CreateChannelRequest = z.object({
    name: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .meta({ label: 'Name' }),
    outboundIdentity: z.string().email('Enter a valid email address').optional().meta({
        label: 'Outbound identity',
        description: 'Email address replies appear to come from.',
    }),
    status: ChannelAccountStatus.default('active').meta({
        label: 'Status',
        options: [
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'reauth_required', label: 'Reauth required' },
            { value: 'error', label: 'Error' },
        ],
    }),
    config: ChannelConfigUnion.meta({ label: 'Configuration' }),
});
// PATCH: kind is immutable. Config patch is a free-form record merged
// server-side onto the existing jsonb (shallow merge — dirty semantics for
// secrets: omit a key → preserve existing value).
export const UpdateChannelRequest = z.object({
    name: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .optional()
        .meta({ label: 'Name' }),
    outboundIdentity: z
        .string()
        .email('Enter a valid email address')
        .nullable()
        .optional()
        .meta({ label: 'Outbound identity' }),
    status: ChannelAccountStatus.optional().meta({
        label: 'Status',
        options: [
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'reauth_required', label: 'Reauth required' },
            { value: 'error', label: 'Error' },
        ],
    }),
    config: z.record(z.string(), z.unknown()).optional().meta({ label: 'Configuration' }),
});
/**
 * OBSERVED health of a channel, written only by the channel-health detector.
 * Orthogonal to `status` (operator intent): a channel can be `active` and
 * `auth_failed` at the same time — which is exactly the state Gmail was in
 * from 27 Jul to 11 Aug 2026 while the admin UI showed a green «Active».
 */
export const ChannelHealthStatus = z.enum(['ok', 'auth_failed', 'failing', 'stalled', 'silent']);
export const ChannelAccountDTO = z.object({
    id: z.guid(),
    kind: ChannelKind,
    name: z.string(),
    outboundIdentity: z.string().nullable(),
    status: ChannelAccountStatus,
    /**
     * Defaulted, NOT required: during a rolling deploy the freshly-served SPA
     * bundle can talk to an admin-api that predates these columns (or one whose
     * migration has not run yet). Without the default such a response would make
     * every channel read as `health !== 'ok'` and paint the whole list red.
     */
    health: ChannelHealthStatus.default('ok'),
    /** When the current health state was entered. */
    healthSince: z.string().datetime().nullable().default(null),
    /** Last successful poll cycle, mirrored out of the pruned sync-run log. */
    lastSuccessAt: z.string().datetime().nullable().default(null),
    lastError: z.string().nullable(),
    config: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ---- Channel sync-run history ------------------------------------------
//
// Durable per-poll run log surfaced read-only in the admin channel view. One
// row per poll cycle (success / partial / failed), newest first.
export const ChannelSyncRunDTO = z.object({
    id: z.guid(),
    channelAccountId: z.guid(),
    trigger: z.string(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    status: z.string(),
    fetched: z.number().int(),
    ingested: z.number().int(),
    skipped: z.number().int(),
    errored: z.number().int(),
    cursorBefore: z.string().nullable(),
    cursorAfter: z.string().nullable(),
    error: z.string().nullable(),
});
export const ListChannelSyncRunsResponse = z.object({
    runs: z.array(ChannelSyncRunDTO),
});
// ---- Google OAuth one-click flow ---------------------------------------
//
// Avoids making admins paste clientId/clientSecret/refreshToken manually. The
// admin-api owns a single Google OAuth app (env: GOOGLE_OAUTH_CLIENT_ID/SECRET
// /REDIRECT_URI), the form only asks for a display name, and a "Connect
// Google" button kicks off the consent flow. Each successful callback creates
// (or re-authorizes) one channel_account tied to whichever mailbox the user
// picks at the Google consent screen — so N clicks yields N independent
// Gmail channels.
export const StartGoogleOAuthRequest = z.object({
    name: z
        .string()
        .min(1, 'Required')
        .max(128, 'Must be 128 characters or fewer')
        .meta({ label: 'Name' }),
});
export const StartGoogleOAuthResponse = z.object({
    channelId: z.guid(),
    authUrl: z.string().url(),
});
export const ReconnectGoogleResponse = z.object({
    authUrl: z.string().url(),
});
//# sourceMappingURL=channels.js.map